import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { homeDisplayApi } from "@/modules/homeDisplay/api";
import type { ProtocolContent } from "@/modules/homeDisplay/api";
import "./ProtocolDialog.scss";
import type { ProtocolDialogProps } from "./ProtocolDialog.types";
import PageLoading from "../PageLoading";
import { Markdown } from "../Markdown/Markdown";
import { useT } from "@/i18n/hooks";

export const ProtocolDialog: React.FC<ProtocolDialogProps> = ({
  visible,
  onClose,
  protocolPath,
}) => {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [protocol, setProtocol] = useState<ProtocolContent | null>(null);
  const [error, setError] = useState<string>("");

  // 加载协议内容
  useEffect(() => {
    if (visible && protocolPath) {
      const loadProtocol = async () => {
        setLoading(true);
        setError("");
        try {
          const data = await homeDisplayApi.getProtocol(protocolPath);
          setProtocol(data);
        } catch (err) {
          console.error("Failed to load protocol:", err);
          setError("Failed to load protocol content");
        } finally {
          setLoading(false);
        }
      };
      loadProtocol();
    }
  }, [visible, protocolPath]);

  // 控制页面滚动
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  if (!visible) return null;

  const container = document.getElementById("root") || document.body;

  return createPortal(
    <div className="protocol-dialog-overlay" onClick={onClose}>
      <div
        className="protocol-dialog"
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
      >
        <div className="protocol-dialog__header">
          <h2 className="protocol-dialog__title">
            {protocol?.name || "Loading..."}
          </h2>
        </div>

        <div className="protocol-dialog__content">
          {loading && (
            <div className="protocol-dialog__loading">
              <PageLoading />
            </div>
          )}

          {error && (
            <div className="protocol-dialog__error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && protocol && (
            <div className="protocol-dialog__html-content">
              <Markdown source={protocol.content} />
            </div>
          )}
        </div>

        <div className="protocol-dialog__btn-group">
          <button
            type="button"
            className="protocol-dialog__btn protocol-dialog__btn--close"
            onClick={onClose}
          >
            {t("common.close")}
          </button>
          <button
            type="button"
            className="protocol-dialog__btn protocol-dialog__btn--ok"
            onClick={onClose}
          >
            {t("common.ok")}
          </button>
        </div>
      </div>
    </div>,
    container
  );
};

export default ProtocolDialog;
